import { Children, Fragment, ReactNode, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import '~/assets/fonts/GTWalsheimPro/stylesheet.css';
import '~/assets/fonts/Roboto/stylesheet.css';
import '~/assets/styles.css';
import AdminPanel from '~/layouts/adminPanel';
import BanScreen from '~/layouts/banScreen';
import Bank from '~/layouts/bank';
import BarberShop from '~/layouts/barberShop';
import CarMarket from '~/layouts/carMarket';
import CarPaint from '~/layouts/carPaint';
import CatchRing from '~/layouts/catchRing';
import CharacterCreation from '~/layouts/characterCreation';
import CharacterEditor from '~/layouts/characterEditor';
import CharacterSelect from '~/layouts/characterSelect';
import ClipboardCopy from '~/layouts/clipboardCopy';
import ClosedBeta from '~/layouts/closedBeta';
import ClothesShop from '~/layouts/clothesShop';
import ClothesTester from '~/layouts/clothesTester';
import CoreLayout from '~/layouts/coreLayout';
import DeathScreen from '~/layouts/deathScreen';
import Dialogs from '~/layouts/dialogs';
import Documents from '~/layouts/documents';
import DonateItem from '~/layouts/donateItem';
import DrivingSchool from '~/layouts/drivingSchool';
import Feedback from '~/layouts/feedback';
import FeedbackLight from '~/layouts/feedbackLight';
import GameFruits from '~/layouts/gameFruits';
import GasStation from '~/layouts/gasStation';
import GreenScreen from '~/layouts/greenScreen';
import Handling from '~/layouts/handling';
import Hedit from '~/layouts/hedit';
import HelpModal from '~/layouts/helpModal';
import HobbyFishing from '~/layouts/hobbyFishing';
import HomeBuying from '~/layouts/homeBuying';
import HousesEditor from '~/layouts/housesEditor';
import Hud from '~/layouts/hud';
import Jobs from '~/layouts/jobs';
import Login from '~/layouts/login';
import LootBuyers from '~/layouts/lootBuyers';
import MainMenu from '~/layouts/mainMenu';
import Modal from '~/layouts/modal';
import Notification from '~/layouts/notification';
import Offers from '~/layouts/offers';
import Phone from '~/layouts/phone';
import PhotoMode from '~/layouts/photoMode';
import PlateBuying from '~/layouts/plateBuying';
import PlayerVehicles from '~/layouts/playerVehicles';
import ProductShop from '~/layouts/productShop';
import Promo from '~/layouts/promo';
import PropertyExchange from '~/layouts/propertyExchange';
import RadialMenu from '~/layouts/radialMenu';
import RockPaperScissors from '~/layouts/rockPaperScissors';
import Runcode from '~/layouts/runcode';
import Screen from '~/layouts/screen';
import ServeIndex from '~/layouts/serveIndex';
import ServerPassword from '~/layouts/serverPassword';
import SpawnSelect from '~/layouts/spawnSelect';
import Tablet from '~/layouts/tablet';
import TattooShop from '~/layouts/tattooShop';
import TechTuning from '~/layouts/techTuning';
import Tuning from '~/layouts/tuning';
import TuningStudio from '~/layouts/tuningStudio';
import TuningTester from '~/layouts/tuningTester';
import VehiclePassport from '~/layouts/vehiclePassport';
import VehicleRegistration from '~/layouts/vehicleRegistration';
import VehicleService from '~/layouts/vehicleService';
import VehicleShop from '~/layouts/vehicleShop';
import BetaRewards from '~/layouts/betaRewards';
import Workbench from '~/layouts/workbench';
import { preloadCommonSounds, setSoundVolume } from './assets/sounds';
import {
	useBackendReady,
	useDisableDrag,
	useDisableSelection,
	useDocumentZoom,
	useHudVisibilityTracker,
	useInputActive,
} from './hooks';
import { LayoutProps } from './rage/utils';

const AppWrapper = ({ children }: { children: React.ReactNode }) => {
	const layoutsToDisplay = useMemo(
		() =>
			Children.map(children, (child: ReactNode, index) => {
				const component = Children.only(child);
				const props = (component as any).type as LayoutProps;
				if (!props.displayName) {
					throw new Error(`Layout name is not defined (index: ${index})`);
				}

				return {
					component,
					props,
				};
			})!,
		[children],
	);

	useHudVisibilityTracker(layoutsToDisplay.map((e) => e.props));

	return (
		<Fragment>
			{layoutsToDisplay.map(({ component, props }, index) => (
				<CoreLayout props={props} key={`layout#${index}`}>
					{component}
				</CoreLayout>
			))}
		</Fragment>
	);
};

const App = () => {
	const backendReady = useBackendReady();
	useDocumentZoom();
	useInputActive();
	useDisableDrag({ allowDraggableElements: true });
	useDisableSelection();

	useEffectOnce(() => {
		setSoundVolume(0.3);
		preloadCommonSounds();
	});

	if (!backendReady) {
		return <div>Loading...</div>;
	}

	return (
		<Fragment>
			<ServeIndex />
			<ClipboardCopy />
			<AppWrapper>
				<Hud />
				<GasStation />
				<PlayerVehicles />
				<Login />
				<ServerPassword />
				<ClosedBeta />
				<BanScreen />
				<VehicleShop />
				<Bank />
				<Hedit />
				<Handling />
				<RadialMenu />
				<Jobs />
				<DrivingSchool />
				<PhotoMode />
				<PropertyExchange />
				<PlateBuying />
				<CharacterCreation />
				<CharacterEditor />
				<HomeBuying />
				<ClothesShop />
				<CarPaint />
				<Tuning />
				<TuningStudio />
				<VehiclePassport />
				<Documents />
				<Offers />
				<CarMarket />
				<VehicleService />
				<Dialogs />
				<DeathScreen />
				<MainMenu />
				<BarberShop />
				<TattooShop />
				<ProductShop />
				<TechTuning />
				<LootBuyers />
				<VehicleRegistration />
				<HobbyFishing />
				<GameFruits />
				<CatchRing />
				<Tablet />
				<Workbench />
				<Phone />
				<RockPaperScissors />
				<CharacterSelect />
				<SpawnSelect />
				<AdminPanel />
				<Screen />
				<Feedback />
				<FeedbackLight />
				<HelpModal />
				<Modal />
				<Notification />
				<GreenScreen />
				<ClothesTester />
				<HousesEditor />
				<TuningTester />
				<Runcode />
				<Promo />
				<BetaRewards />
				<DonateItem />
			</AppWrapper>
		</Fragment>
	);
};

export default App;
